import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import DateUtils from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/DateUtils";
import { useResponsive } from "@/hooks/useResponsive";
import { DataRoomFile } from "@/models/data-room/DataRoomFile";
import { DataRoomRedFlag } from "@/models/data-room/DataRoomRedFlag";
import { DataRoomSmallFile } from "@/models/data-room/DataRoomSmallFile";
import { DataRoomLazoModalSx } from "@/pages/OneDataRoomPage";
import DataRoomService from "@/services/DataRoomService";
import RedFlagService from "@/services/RedFlagService";
import { extractExtensionRemoveDot } from "@/utils/FilenameUtils";
import { AddOutlined, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Box,
  FormLabel,
  MenuItem,
  SxProps,
  Tab,
  TextField,
  Tooltip,
  TooltipProps,
  Typography,
  buttonBaseClasses,
  styled,
} from "@mui/material";
import { RowSelectionState, SortDirection } from "@tanstack/react-table";
import moment from "moment";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import FilePreview from "../common/FilePreview";
import { LazoCheckbox } from "../common/form/LazoCheckbox";
import CommonButton, { CommonButtonProps, CommonButtonSecondarySx } from "../utils/CommonButton";
import CommonFormControl from "../utils/CommonFormControl";
import CommonTabs from "../utils/CommonTabs";
import { FileTableMenu, FileTableMoreHoriz } from "../utils/FileTable/FileTableStyles";
import LazoModal from "../utils/LazoModal";
import DataRoomAISummary from "./DataRoomAISummary";
import { TREE_FILTERS } from "./DataRoomDirectoryFileTree";
import DataRoomRedFlagsEmptyScreen from "./DataRoomRedFlagsEmptyScreen";
import DataRoomRedFlagsTable from "./DataRoomRedFlagsTable";
import { useDataRoomProvider } from "./contexts/DataRoomContext";

type DateTimeString = string;
export type PreSignedMapType = {
  [ext: string]: { url: string; expiresAt: DateTimeString };
};

enum SummaryTab {
  AI_ANALYSIS = "Ai Analysis",
  ABOUT = "About",
  RED_FLAGS = "Red Flags",
}

type SortField = "name" | "createdAt";

const EXT_COLORS: Record<string, { bg: string; color: string }> = {
  PDF: { bg: "#FFEAEA", color: Colors.HEART_RED },
  DOCX: { bg: "#EEF0FF", color: Colors.BLUE_4763F4 },
  DOC: { bg: "#EEF0FF", color: Colors.BLUE_4763F4 },
  XLSX: { bg: "#EDFFF0", color: Colors.GREEN_01A902 },
  XLS: { bg: "#EDFFF0", color: Colors.GREEN_01A902 },
  PPTX: { bg: "#FFF4E5", color: Colors.ORANGE_FF9A3D },
  PPT: { bg: "#FFF4E5", color: Colors.ORANGE_FF9A3D },
};

function getExtColor(ext: string) {
  return EXT_COLORS[ext.toUpperCase()] ?? { bg: Colors.LIGHT_GRAY_DEDEE1, color: Colors.LIGHT_GRAY_8A8E93 };
}

interface DataRoomFolderFilesTableProps {
  setFiles: Dispatch<SetStateAction<DataRoomFile[]>>;
  files: DataRoomFile[];
  selected: DataRoomFile[];
  setSelected: (v: DataRoomFile[]) => void;
  rowSelection: RowSelectionState;
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
  fetchTreeStats?: () => void;
  onAskAI?: (file: DataRoomFile) => void;
  focusedFile?: DataRoomFile;
  setFocusedFile?: Dispatch<SetStateAction<DataRoomFile>>;
}

export default function DataRoomFolderFilesTable(props: DataRoomFolderFilesTableProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuFile, setMenuFile] = useState<null | DataRoomFile>(null);
  const menuOpen = Boolean(menuAnchor);

  const { selectedCustom, treeFilter } = useDataRoomProvider();
  const { isMobile, isTablet } = useResponsive();

  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  useEffect(() => {
    if (!props.focusedFile) return;
    setMenuFile(props.focusedFile);
    setOpenDetailsModal(true);
  }, [props.focusedFile]);

  const [downloading, setDownloading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState<boolean>(false);
  const [summaryTab, setSummaryTab] = useState<SummaryTab>(SummaryTab.AI_ANALYSIS);
  const [showAddRedFlag, setShowAddRedFlag] = useState<boolean>(false);
  const [redFlagName, setRedFlagName] = useState<string>("");
  const [creatingRedFlag, setCreatingRedFlag] = useState<boolean>(false);

  let preSignedMap: PreSignedMapType = JSON.parse(sessionStorage.getItem("DataRoom_PreSignedMap")) ?? {};
  useEffect(() => generatePreSignedUrl(menuFile), [menuFile]);

  const [preSignedMapState, setPreSignedMapState] = useState<PreSignedMapType>(preSignedMap);
  function generatePreSignedUrl(file: DataRoomFile) {
    if (!file?.externalId) return;
    if (preSignedMap[file.externalId] && moment().isBefore(moment(preSignedMap[file.externalId].expiresAt))) return;
    DataRoomService.getPreSignedUrl(file.externalId).then((r) => {
      r.onSuccess((u) => {
        const urlObject = new URL(u);
        const sparams = new URLSearchParams(urlObject.search);
        const seconds = Number(sparams.get("X-Amz-Expires"));
        let expiration: DateTimeString = null;
        if (!seconds) {
          expiration = new Date(Number(sparams.get("Expires")) * 1000).toString();
        } else {
          expiration = moment().add(seconds, "seconds").toString();
        }
        preSignedMap = { ...preSignedMap, [file.externalId]: { url: u, expiresAt: expiration } };
        sessionStorage.setItem("DataRoom_PreSignedMap", JSON.stringify(preSignedMap));
        setPreSignedMapState(preSignedMap);
      });
    });
  }

  const [openDetailsModal, setOpenDetailsModal] = useState<boolean>(false);

  // Sorting
  const sortedFiles = useMemo(() => {
    return [...props.files].sort((a, b) => {
      const av = a[sortField] ?? "";
      const bv = b[sortField] ?? "";
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [props.files, sortField, sortDir]);

  function handleSortClick(field: SortField) {
    if (sortField === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  // Selection
  const allSelected = props.files.length > 0 && props.files.every((_, idx) => props.rowSelection[idx]);
  const someSelected = !allSelected && props.files.some((_, idx) => props.rowSelection[idx]);

  function handleSelectAll(checked: boolean) {
    if (checked) {
      const all: RowSelectionState = {};
      props.files.forEach((_, idx) => {
        all[idx] = true;
      });
      props.setRowSelection(all);
    } else {
      props.setRowSelection({});
    }
  }

  function handleCheckFile(checked: boolean, originalIdx: number) {
    props.setRowSelection((prev) => {
      const next = { ...prev };
      if (checked) next[originalIdx] = true;
      else delete next[originalIdx];
      return next;
    });
  }

  function handleMoreClick(ev: React.MouseEvent<HTMLElement>, file: DataRoomFile) {
    setMenuAnchor(ev.currentTarget);
    setMenuFile(file);
  }

  function handleMenuClose() {
    setMenuAnchor(null);
    setMenuFile(null);
  }

  function downloadFiles() {
    setDownloading(true);
    const fExternalIds = props.selected?.length === 0 ? [menuFile.externalId] : props.selected.map((f) => f.externalId);
    let filename = "file";
    if (fExternalIds.length === 1) filename = menuFile.name;
    DataRoomService.downloadFiles(fExternalIds, filename)
      .then(() => handleMenuClose())
      .catch((e) => {
        NotificationService.error("Couldn't download files");
        console.log("Error: ", +e);
      })
      .finally(() => setDownloading(false));
  }

  function deleteFiles(): void {
    setDeleting(true);
    let delArr: string[] = [];
    if (Object.keys(props.rowSelection).length > 0) {
      delArr = Object.keys(props.rowSelection).map((idx) => props.files[idx].externalId);
    } else {
      delArr = [menuFile.externalId];
    }
    DataRoomService.deleteFiles(delArr, treeFilter === TREE_FILTERS.CUSTOM && selectedCustom?.externalId)
      .then((r) => {
        r.onSuccess((_) => {
          NotificationService.info("File(s) deleted");
          props.setFiles((prev) => prev.filter((f) => !delArr.includes(f.externalId)));
          props.setRowSelection({});
          props.fetchTreeStats?.();
          handleMenuClose();
          setOpenConfirmDeleteModal(false);
        });
        r.onError((e) => {
          NotificationService.error("Error deleting files");
          console.log("error: " + e);
        });
      })
      .finally(() => setDeleting(false));
  }

  function handleDeleteClick(): void {
    setOpenConfirmDeleteModal(true);
  }

  function createRedFlag() {
    if (redFlagName === "" || !redFlagName || creatingRedFlag) return;
    setCreatingRedFlag(true);
    RedFlagService.createRedFlag({ name: redFlagName, file: menuFile as DataRoomSmallFile } as DataRoomRedFlag)
      .then((r) => {
        r.onSuccess((c) => {
          props.setFiles((prev) =>
            prev.map((f) => {
              if (f.externalId === menuFile.externalId) {
                const updated = { ...f, redFlags: [...f.redFlags, c] };
                setMenuFile(updated);
                return updated;
              }
              return f;
            })
          );
          setShowAddRedFlag(false);
          setRedFlagName("");
        });
        r.onError((e) => {
          NotificationService.error(e);
          console.log("error: " + e);
        });
      })
      .finally(() => setCreatingRedFlag(false));
  }

  function handleFlagsUpdate(updated: DataRoomRedFlag[]) {
    props.setFiles((prev) =>
      prev.map((f) => {
        if (f.externalId === menuFile.externalId) return { ...menuFile, redFlags: updated };
        return f;
      })
    );
    setMenuFile({ ...menuFile, redFlags: updated });
  }

  function handleNewFlagKeyDown(ev: React.KeyboardEvent<HTMLDivElement>) {
    if ((ev as any).key === "Enter") createRedFlag();
  }

  function handleDetailsClose() {
    setOpenDetailsModal(false);
    setMenuFile(null);
    setMenuAnchor(null);
  }

  function handleAskAIClick() {
    props.onAskAI?.(menuFile);
    handleMenuClose();
  }

  return (
    <>
      <FileListWrapper>
        {/* Header sticky */}
        <FileListHeader>
          <StyledLazoCheckbox checked={allSelected} indeterminate={someSelected} onChange={(e) => handleSelectAll(e.target.checked)} />
          <HeaderLabel onClick={() => handleSortClick("name")}>
            Name
            {sortField === "name" && (sortDir === "asc" ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />)}
          </HeaderLabel>
          <HeaderLabel onClick={() => handleSortClick("createdAt")}>
            Date Added
            {sortField === "createdAt" &&
              (sortDir === "asc" ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />)}
          </HeaderLabel>
          <Box />
        </FileListHeader>

        {/* Cards */}
        {sortedFiles.map((file) => {
          const originalIdx = props.files.indexOf(file);
          const isSelected = !!props.rowSelection[originalIdx];
          const isFocused = props.focusedFile?.externalId === file.externalId;
          const ext = extractExtensionRemoveDot(file.name).toUpperCase();
          const extColors = getExtColor(ext);

          return (
            <FileCard
              key={file.externalId}
              isSelected={isSelected}
              isFocused={isFocused}
              onMouseOver={() => isFocused && props.setFocusedFile?.(null)}
            >
              <StyledLazoCheckbox
                checked={isSelected}
                onChange={(e) => handleCheckFile(e.target.checked, originalIdx)}
                onClick={(e) => e.stopPropagation()}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
                <ExtBadge extbg={extColors.bg} extcolor={extColors.color}>
                  {ext}
                </ExtBadge>
                <FileNameTypography
                  onClick={() => {
                    setMenuFile(file);
                    setOpenDetailsModal(true);
                  }}
                >
                  {file.name}
                </FileNameTypography>
              </Box>
              <DateTypography>{file.createdAt ? DateUtils.formatAsMMDDYYY(file.createdAt as unknown as Date) : "-"}</DateTypography>
              <Box
                className="row-actions"
                sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
                onClick={(ev) => handleMoreClick(ev, file)}
              >
                <FileTableMoreHoriz />
              </Box>
            </FileCard>
          );
        })}
      </FileListWrapper>

      <FileTableMenu anchorEl={menuAnchor} open={menuOpen} onClose={handleMenuClose}>
        <MenuItem onClick={() => setOpenDetailsModal(true)}>
          <Typography>Open</Typography>
        </MenuItem>
        <MenuItem onClick={handleAskAIClick}>
          <Typography>Ask AI</Typography>
        </MenuItem>
        <MenuItem disabled={downloading} onClick={downloadFiles}>
          <Typography>Download</Typography>
        </MenuItem>
        <MenuItem disabled={deleting} onClick={handleDeleteClick}>
          <Typography color="red">Delete</Typography>
        </MenuItem>
      </FileTableMenu>

      <LazoModal
        sx={DataRoomLazoModalSx}
        open={openConfirmDeleteModal}
        title={"Confirm delete"}
        handleClose={() => setOpenConfirmDeleteModal(false)}
        buttons={[
          <CommonButton
            style={{ borderRadius: "0.5rem" }}
            purpleBorderVariant
            onClick={() => setOpenConfirmDeleteModal(false)}
            key="cancel-button"
          >
            Cancel
          </CommonButton>,
          <CommonButton
            style={{ borderRadius: "0.5rem", backgroundColor: Colors.HEART_RED }}
            onClick={deleteFiles}
            disabled={deleting}
            key="delete-button"
          >
            {!deleting ? "Delete" : <Spinner size="1.5rem" />}
          </CommonButton>,
        ]}
      >
        <Typography fontSize="0.75rem" fontWeight="400" lineHeight="18px" color={Colors.LIGHT_GRAY_8A8E93}>
          Are you sure you want to delete this file?
        </Typography>
      </LazoModal>

      {openDetailsModal && (
        <LazoModal
          open={openDetailsModal}
          handleClose={handleDetailsClose}
          title={menuFile?.name}
          sx={{
            "& .title-container": {
              alignSelf: "flex-start",
              width: "100%",
              justifyContent: "space-between",
            },
          }}
        >
          <SummaryContainer>
            <PreviewContainer>
              {preSignedMapState[menuFile?.externalId]?.url ? (
                <FilePreview
                  isPreviewModal={true}
                  fileUrl={preSignedMapState[menuFile.externalId].url}
                  fileType={extractExtensionRemoveDot(menuFile?.name)}
                  viewportWidth="100%"
                  viewportHeight={isMobile ? "45vh" : isTablet ? "50vh" : "80vh"}
                />
              ) : (
                <Spinner size="2rem" sx={{ color: Colors.LAZO_PURPLE }} />
              )}
            </PreviewContainer>
            <SummaryDataContainer>
              <CommonTabs sx={{ ...CommonTabsSx }} value={summaryTab} onChange={(_, t) => setSummaryTab(t)}>
                <Tab sx={{ padding: 0, flex: "1" }} value={SummaryTab.AI_ANALYSIS} label={SummaryTab.AI_ANALYSIS} />
                {false && (
                  <>
                    <Tab sx={{ padding: 0, flex: "1" }} value={SummaryTab.ABOUT} label={SummaryTab.ABOUT} />
                    <Tab sx={{ padding: 0, flex: "1" }} value={SummaryTab.RED_FLAGS} label={SummaryTab.RED_FLAGS} />
                  </>
                )}
              </CommonTabs>
              <TabContext value={summaryTab}>
                {summaryTab === SummaryTab.AI_ANALYSIS && (
                  <StyledTabPanel value={SummaryTab.AI_ANALYSIS}>
                    <DataRoomAISummary
                      updateFn={(f, updated) => DataRoomService.updateSummary(f, updated)}
                      file={menuFile}
                      setFile={(u) => setMenuFile(u as DataRoomFile)}
                    />
                  </StyledTabPanel>
                )}
                {summaryTab === SummaryTab.ABOUT && <StyledTabPanel value={SummaryTab.ABOUT} />}
                {summaryTab === SummaryTab.RED_FLAGS && (
                  <StyledTabPanel sx={{ height: "100%" }} value={SummaryTab.RED_FLAGS}>
                    <>
                      <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                        <Typography fontWeight="600" fontSize="0.75rem" color={Colors.LIGHT_GRAY_8A8E93}>
                          Red Flags
                        </Typography>
                        <StyledCommonButton onClick={() => setShowAddRedFlag(!showAddRedFlag)}>
                          <AddOutlined />
                          <Typography fontSize="0.75rem" fontWeight="500">
                            Add
                          </Typography>
                        </StyledCommonButton>
                      </Box>
                      {showAddRedFlag ? (
                        <>
                          <CommonFormControl fullWidth>
                            <FormLabel>Red Flag name</FormLabel>
                            <TextField
                              value={redFlagName}
                              onKeyDown={handleNewFlagKeyDown}
                              onChange={(e) => setRedFlagName(e.target.value)}
                            />
                          </CommonFormControl>
                          <Box
                            display="flex"
                            width="100%"
                            alignItems="center"
                            justifyContent="flex-end"
                            gap="0.75rem"
                            sx={{ flexDirection: { xs: "column", sm: "row" }, gap: { xs: "0.5rem", sm: "0.75rem" } }}
                          >
                            <CommonButton
                              onClick={() => setShowAddRedFlag(false)}
                              purpleBorderVariant
                              sx={{ borderRadius: "0.25rem", minWidth: 0, minHeight: 0, height: "1.75rem", padding: "0 0.5rem" }}
                            >
                              <Typography fontSize="0.75rem" fontWeight="500">
                                Cancel
                              </Typography>
                            </CommonButton>
                            <CommonButton
                              disabled={redFlagName === "" || creatingRedFlag}
                              onClick={createRedFlag}
                              sx={{ borderRadius: "0.25rem", minWidth: 0, minHeight: 0, height: "1.75rem", padding: "0 0.5rem" }}
                            >
                              {creatingRedFlag ? (
                                <Spinner size="1rem" />
                              ) : (
                                <Typography fontSize="0.75rem" fontWeight="500">
                                  Save
                                </Typography>
                              )}
                            </CommonButton>
                          </Box>
                        </>
                      ) : menuFile?.redFlags?.length === 0 ? (
                        <DataRoomRedFlagsEmptyScreen
                          subtext="No red flags for this document"
                          imageSx={{ height: "7.688rem", width: "7.688rem" }}
                        />
                      ) : (
                        <RedFlagsTableContainer>
                          <DataRoomRedFlagsTable hideHeader flags={menuFile.redFlags} onFlagsUpdate={handleFlagsUpdate} hideFix />
                        </RedFlagsTableContainer>
                      )}
                    </>
                  </StyledTabPanel>
                )}
              </TabContext>
            </SummaryDataContainer>
          </SummaryContainer>
        </LazoModal>
      )}
    </>
  );
}

// ─── Styled Components ────────────────────────────────────────────────────────

const FileListWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  overflowY: "auto",
});

// Columns: checkbox | name+badge | date | actions
const GRID_COLUMNS = "1.5rem 1fr 7rem 2.5rem";

const FileListHeader = styled(Box)({
  display: "grid",
  gridTemplateColumns: GRID_COLUMNS,
  alignItems: "center",
  gap: "0.75rem",
  padding: "0.5rem 1rem",
  backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  borderBottom: `1px solid rgba(0,0,0,0.06)`,
  position: "sticky",
  top: 0,
  zIndex: 1,
  flexShrink: 0,
});

const HeaderLabel = styled(Typography)({
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: Colors.LIGHT_GRAY_8A8E93,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  whiteSpace: "nowrap",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "0.2rem",
  userSelect: "none",
  "&:hover": { color: Colors.BLACK_151517 },
});

const FileCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "isFocused",
})<{ isSelected: boolean; isFocused: boolean }>(({ isSelected, isFocused }) => ({
  display: "grid",
  gridTemplateColumns: GRID_COLUMNS,
  alignItems: "center",
  gap: "0.75rem",
  padding: "0 1rem",
  height: "3.5rem",
  borderBottom: `1px solid rgba(0,0,0,0.06)`,
  backgroundColor: isFocused ? Colors.LIGHT_GREEN_DESATURATED_FCFFEC : isSelected ? Colors.BUTTON_SECONDARY_HOVER_PURPLE : "transparent",
  transition: "background-color 0.15s ease",
  "& .row-actions": {
    opacity: 0,
    transition: "opacity 0.15s ease",
  },
  "&:hover": {
    backgroundColor: isSelected ? Colors.BUTTON_SECONDARY_HOVER_PURPLE : Colors.LIGHT_GRAY_BG_F6F8FA,
    "& .row-actions": {
      opacity: 1,
    },
  },
}));

const ExtBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "extbg" && prop !== "extcolor",
})<{ extbg: string; extcolor: string }>(({ extbg, extcolor }) => ({
  backgroundColor: extbg,
  color: extcolor,
  borderRadius: "0.3rem",
  padding: "0.15rem 0.4rem",
  fontSize: "0.625rem",
  fontWeight: 700,
  lineHeight: "1rem",
  textAlign: "center",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
}));

const FileNameTypography = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 600,
  lineHeight: "20px",
  color: Colors.BLACK_151517,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  cursor: "pointer",
  transition: "color 0.15s ease",
  "&:hover": { color: Colors.LAZO_PURPLE },
});

const DateTypography = styled(Typography)({
  fontSize: "0.75rem",
  fontWeight: 400,
  color: Colors.LIGHT_GRAY_8A8E93,
});

const StyledTooltip = (props: TooltipProps) => <Tooltip {...props} componentsProps={{ tooltip: { sx: { fontSize: "0.875rem" } } }} />;

const SummaryContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "1.5rem",
  width: "100%",
  maxWidth: "1400px",
  maxHeight: "90vh",
  overflow: "hidden",
  flexDirection: "row",
  [theme.breakpoints.down("md")]: { width: "100%", gap: "1rem", flexDirection: "column" },
  [theme.breakpoints.down("sm")]: { gap: "0.75rem", flexDirection: "column" },
}));

const SummaryDataContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  height: "100%",
  width: "100%",
  flexDirection: "column",
  gap: "1rem",
  alignItems: "center",
  overflowY: "auto",
  minHeight: 0,
  [theme.breakpoints.up("md")]: { width: "35%", minWidth: "320px", height: "80vh" },
  [theme.breakpoints.down("md")]: { height: "50vh", minHeight: "400px" },
  [theme.breakpoints.down("sm")]: { height: "40vh", minHeight: "300px", gap: "0.75rem" },
}));

const PreviewContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  minHeight: 0,
  overflow: "hidden",
  [theme.breakpoints.up("md")]: { width: "45vw", height: "80vh" },
}));

const CommonTabsSx: SxProps = {
  width: "100%",
  minHeight: { xs: "2.5rem", sm: "2.250rem" },
  height: { xs: "2.5rem", sm: "2.250rem" },
  padding: 0,
  "& .MuiTab-root": {
    maxWidth: "100%",
    fontSize: { xs: "0.75rem", sm: "0.875rem" },
    padding: { xs: "0.5rem 0.25rem", sm: "0.75rem 1rem" },
    minWidth: { xs: "auto", sm: "90px" },
  },
  "& .MuiTabs-flexContainer": { height: "100%", justifyContent: { xs: "space-around", sm: "flex-start" } },
  "& .MuiTabs-indicator": { height: { xs: "2px", sm: "3px" } },
};

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
  display: "flex",
  width: "100%",
  alignItems: "center",
  flexDirection: "column",
  gap: "0.75rem",
  padding: 0,
  [theme.breakpoints.down("sm")]: { gap: "0.5rem", padding: "0 0.25rem" },
}));

const StyledCommonButton = (props: CommonButtonProps) => (
  <CommonButton
    {...props}
    sx={{
      ...CommonButtonSecondarySx,
      height: { xs: "2rem", sm: "1.75rem" },
      minWidth: "fit-content",
      padding: { xs: "0 0.75rem", sm: "0 0.5rem" },
      fontSize: { xs: "0.75rem", sm: "0.875rem" },
    }}
  />
);

const RedFlagsTableContainer = styled(Box)(() => ({
  display: "flex",
  overflowY: "auto",
  maxHeight: "32.5rem",
}));

const StyledLazoCheckbox = styled(LazoCheckbox)(() => ({
  [`&.${buttonBaseClasses.root}`]: { padding: 0 },
}));
